/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 *
 * ConfirmRemoveStudy — Confirmation modal for removing an EOS/PDR study with comment input.
 * Migrated from: lego/page/eos/transfer-media/ConfirmRemoveStudy.jsx
 */

import { useState } from 'react'
import { useServiceContext } from '../../../root'
import Modal from '../../../components/Modal'
import type { ConfirmRemoveStudyProps } from './types'

const MAX_COMMENT_LENGTH = 254

/**
 * Formats text area counter display.
 * Replaces legacy Utils.formatTextAreaCounter.
 */
const formatTextAreaCounter = (length: number, max: number, label: string): string => {
  return `${length}/${max} ${label}`
}

const ConfirmRemoveStudy: React.FC<ConfirmRemoveStudyProps> = ({ ftpConfig, isPdrFlow }) => {
  const [isOpen, setIsOpen] = useState(true)
  const [removeComment, setRemoveComment] = useState<string | null>(null)
  const context = useServiceContext()
  const { translate } = context.service.filter

  const doClose = () => {
    setIsOpen(false)
  }

  const removeEosStudy = () => {
    const ftpConfigData = {
      ...ftpConfig,
      id: ftpConfig.id ?? undefined,
      comment: removeComment ?? undefined
    }
    context.api.eos.remove(ftpConfigData).then(
      () => {
        context.service.toast.success(
          isPdrFlow ? 'PDR_TRANSFER_MEDIA_PDR_DELETE_SUCCESS_MSG' : 'EOS_TRANSFER_MEDIA_EOS_DELETE_SUCCESS_MSG'
        )
        context.$rootScope.$emit('studyRemovalSuccess', ftpConfig)
        doClose()
      },
      (data) => {
        context.service.toast.error(data?.body?.message ?? data?.data?.message)
        doClose()
      }
    )
  }

  return (
    <Modal
      show={isOpen}
      handleClose={doClose}
      title={translate('EOS_TRANSFER_MEDIA_EOS_DELETE_TITLE')}
      isLevelTwoModal
      className="level-one-modal remove-study-modal"
    >
      <Modal.Body>
        <p>
          {translate(isPdrFlow ? 'PDR_TRANSFER_MEDIA_PDR_DELETE_Q' : 'EOS_TRANSFER_MEDIA_EOS_DELETE_Q')}
          <b>{' ' + ftpConfig.name}</b>
        </p>
        <div className="fs12 commment-title">{translate('COMMENT')}</div>
        <div className="md-form-group">
          <textarea
            className="md-textarea"
            maxLength={MAX_COMMENT_LENGTH}
            value={removeComment ?? ''}
            name="description"
            onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
              setRemoveComment(event?.target?.value?.length ? event.target.value : null)
            }}
            id="in-f"
            rows={6}
          />
          <span className="textarea-counter">
            {formatTextAreaCounter((removeComment ?? '').length, MAX_COMMENT_LENGTH, translate('CHAR_COUNT_LIMIT'))}
          </span>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Button variant="link" onClick={doClose}>
          {translate('NO')}
        </Modal.Footer.Button>
        <Modal.Footer.Button variant="primary" onClick={removeEosStudy}>
          {translate('YES')}
        </Modal.Footer.Button>
      </Modal.Footer>
    </Modal>
  )
}

export default ConfirmRemoveStudy
